import { Injectable } from '@angular/core';

import { BaseApi } from './base/base.api';
import { firstValueFrom } from 'rxjs';
import { CreatePreOrderCalculation } from '@/models/pre-order-calculation.model';
import { ArrayCreateGenericViability, ArrayGetGenericViability, CreateStartOperation, Proposal } from '@/models/proposal-order.model';
import { CreatePreOrder, Order } from '@/models/order.model';
import { Filter } from '@/models/filter.model';
import { Pagination } from '@/models/pagination.model';
import { HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export abstract class OrderApi extends BaseApi {

  async orders(filter?: Filter): Promise<Pagination<Order>> {
    let params = new HttpParams();
    if (filter) {
      Object.keys(filter).forEach(key => {
        if(filter[key as keyof Filter]){
            params = params.append(key, filter[key as keyof Filter].toString());
        }
      });
    }
    const response = await firstValueFrom(this.http.get<{ succeeded: boolean, data: Pagination<Order> }>(
      `${this.url}/proposals`, { params } ));

    return response.data;
  }

  async orderSearch(search: string): Promise<Pagination<Order>> {
    if (search) {
      const response = await firstValueFrom(this.http.get<{ succeeded: boolean, data: Pagination<Order> }>(
        `${this.url}/proposals?search=${encodeURIComponent(search)}`));

      return response.data
    } else {
      return this.orders();
    }
  }

  async downloadOrdersExcel(filterData: any): Promise<void> {
    let params = new HttpParams();
    if (filterData) {
      Object.keys(filterData).forEach((key) => {
        const value = filterData[key as keyof Filter];
        if (value !== null && value !== undefined) {
          params = params.append(key, value.toString());
        }
      });
    }
  
    try {
      const response = await firstValueFrom(this.http.get(
        `${this.url}/proposals/excel`,
        { params, responseType: 'blob' }
      ));
  
      const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'orders.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar o arquivo:', error);
    }
  }
  

  async getOrderById(orderId: string): Promise<Order> {
    const response = await firstValueFrom(this.http.get<{ succeeded: boolean, data: Order }>(
      `${this.url}/proposals/${orderId}`));

    return response.data;
  }

  deleteOrder(orderId: string): Promise<void> {
    return this.delete(`${this.url}/proposals/${orderId}`)
  }

  async preOrderCalculation(payload: CreatePreOrderCalculation): Promise<any> {
    return this.post(`${this.url}/calculates/pre-order`, payload);
  }

  async preOrder(payload: CreatePreOrder): Promise<any> {
    const response = await firstValueFrom(this.http.post<{ succeeded: boolean, data: any }>(
      `${this.url}/proposals/pre-order`, payload));

    return response.data;
  }

  async updatePreOrder(proposalId: string, payload: CreatePreOrder): Promise<any> {
    const response = await firstValueFrom(this.http.put<{ succeeded: boolean, data: any }>(
      `${this.url}/proposals/pre-order/${proposalId}`, payload));

    return response.data.id;
  }

  async getProposalById(proposalId: string): Promise<Proposal> {
    const response = await firstValueFrom(this.http.get<{ succeeded: boolean, data: Proposal }>(
      `${this.url}/proposals/${proposalId}`));

    return response.data;
  }

  async order(preOrderId: string, payload: any): Promise<any> {
    const response = await firstValueFrom(this.http.post<{ succeeded: boolean, data: any }>(
      `${this.url}/proposals/${preOrderId}/order`, payload));

    return response.data;
  }

  async orderCompleted(proposalId: string): Promise<any> {
    const response = await firstValueFrom(this.http.put<{ succeeded: boolean, data: any }>(
      `${this.url}/proposals/${proposalId}/order/complete`, {}));
    return response.data;
  }

  async getViability(proposalId: string): Promise<ArrayGetGenericViability> {
    const response = await firstValueFrom(this.http.get<{ succeeded: boolean, data: ArrayGetGenericViability }>(
      `${this.url}/proposals/${proposalId}/viability`));

    return response.data
  }

  async getViabilityBySearch(proposalId: string, text: string): Promise<ArrayGetGenericViability> {
    const response = await firstValueFrom(this.http.get<{ succeeded: boolean, data: ArrayGetGenericViability }>(
      `${this.url}/proposals/${proposalId}/viability?search=${text}`));

    return response.data
  }

  async getViabilityStartOperation(proposalId: string): Promise<ArrayGetGenericViability> {
    const response = await firstValueFrom(this.http.get<{ succeeded: boolean, data: ArrayGetGenericViability }>(
      `${this.url}/proposals/${proposalId}/viability?viability=true`));

    return response.data
  }
  async getViabilityStartOperationBySearch(proposalId: string, text: string): Promise<ArrayGetGenericViability> {
    const response = await firstValueFrom(this.http.get<{ succeeded: boolean, data: ArrayGetGenericViability }>(
      `${this.url}/proposals/${proposalId}/viability?viability=true&search=${text}`));

    return response.data
  }

  async getViabilitiesByFlag(flagId: string, proposalId: string): Promise<ArrayGetGenericViability> {
    const response = await firstValueFrom(this.http.get<{ succeeded: boolean, data: ArrayGetGenericViability }>(
      `${this.url}/proposals/${proposalId}/viability?BrandId=${flagId}`));

    return response.data
  }
  async getViabilitiesByState(state: string, proposalId: string): Promise<ArrayGetGenericViability> {
    const response = await firstValueFrom(this.http.get<{ succeeded: boolean, data: ArrayGetGenericViability }>(
      `${this.url}/proposals/${proposalId}/viability?State=${state}`));

    return response.data
  }

  async createViability(proposalId: string, payload: ArrayCreateGenericViability): Promise<any> {
    return this.post(`${this.url}/proposals/${proposalId}/create-viability`, payload);
  }

  async updateViability(proposalId: string, payload: ArrayCreateGenericViability): Promise<any> {
    return this.put(`${this.url}/proposals/${proposalId}/update-viability`, payload);
  }

  async createOperationStart(payload: CreateStartOperation): Promise<any> {
    return this.post(`${this.url}/proposals/operation-start`, payload);
  }

  async nextStep(proposalId: string, nextStep: number): Promise<any> {
    return this.put(`${this.url}/proposals/${proposalId}/next-step`, { nextStep });
  }

  async previousStep(proposalId: string, previousStep: number): Promise<any> {
    return this.put(`${this.url}/proposals/${proposalId}/previous-step`, { previousStep });
  }

  async sendEmail(proposalId: string, email: string): Promise<any> {
    return this.post(`${this.url}/proposals/${proposalId}/send-email?email=${email}`, {});
  }

  async generatePdf(proposalId: string): Promise<any> {
    return this.put(`${this.url}/proposals/${proposalId}/generate-pdf`, { id: proposalId });
  }

}
